import { ExpandedProduct, ProductType } from '@v2/shared/lib/types';
import { isNamedFormat, titleCase } from '@v2/shared/lib/utils';

/** A label whose value should not be shown at all for this product type. */
const REMOVE = '{{remove}}';

/**
 * Genome keys whose human label cannot be derived by title-casing them — acronyms,
 * mostly, plus the two the singular/plural of "author" gets wrong.
 */
const LABELS: Record<string, string> = {
  country_iso2_code: 'Country ISO2 Code',
  country_iso3_code: 'Country ISO3 Code',
  langague_iso2_code: 'Language ISO2 Code',
  langague_iso3_code: 'Language ISO3 Code',
  language_iso2_code: 'Language ISO2 Code',
  language_iso3_code: 'Language ISO3 Code',
  issn: 'ISSN',
  authors: 'Author(s)',
  isbn_13: 'ISBN-13',
  isbn_10: 'ISBN-10',
  gtin_14: 'GTIN-14',
  number_of_issues: 'Number of Issues',
  number_of_pages: 'Number of Pages',
  upc: 'UPC',
};

/**
 * A miscellaneous good is not a book, so the barcode in `isbn_13` is not an ISBN — it
 * is whatever the manufacturer printed, and `isbn_10` is meaningless for it.
 */
const LABELS_BY_TYPE: Record<number, Record<string, string>> = {
  [ProductType.TYPE_MISCELLANEOUS_GOODS]: {
    isbn_13: 'EAN/UPC',
    isbn_10: REMOVE,
  },
};

export type GenomeAttribute = {
  label: string;
  value: unknown;
  /** Set instead of `value` for the author list, which renders as search links. */
  authors?: string[];
};

/**
 * The Details table, from the untyped bag of genome attributes the serializer hands
 * over.
 *
 * Sorted by label rather than left in insertion order: the genome's key order is
 * whatever the feed happened to write, so two books of the same kind would otherwise
 * list their details differently.
 */
export default function normalizeGenomeAttributes(product: ExpandedProduct): GenomeAttribute[] {
  const genome = product.genome_attributes ?? {};
  const overrides = LABELS_BY_TYPE[product.product_type] ?? {};

  return Object.keys(genome)
    .filter((key) => key !== 'format' || isNamedFormat(genome[key]))
    .map((key): GenomeAttribute => {
      const content = genome[key];
      const label = overrides[key] || LABELS[key] || titleCase(key.replace(/_/g, ' '));

      if (key === 'authors' && Array.isArray(content)) {
        return { label, value: null, authors: content };
      }

      return { label, value: Array.isArray(content) ? content.join(' & ') : content };
    })
    .filter((attribute) => attribute.label !== REMOVE && (attribute.value !== null || attribute.authors))
    .sort((a, b) => (a.label > b.label ? 1 : -1));
}
